import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Coins, Phone, CalendarClock, AlertTriangle, Ban, Plus, MessageCircle } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { TabBar } from '@/components/ui/tab-bar';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/toast';

interface BucketItem {
  paymentId: string;
  planId: string;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  invoiceNumber: string | null;
  dueDate: string;
  amountDue: number;
  status: string;
}

interface Bucket {
  count: number;
  totalAmount: number;
  items: BucketItem[];
}

interface DefaulterItem {
  planId: string;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  invoiceNumber: string | null;
  remainingAmount: number;
  nextDueDate: string;
}

interface RecoveryDashboard {
  dueToday: Bucket;
  dueThisWeek: Bucket;
  dueThisMonth: Bucket;
  overdue: Bucket;
  defaulters: { count: number; totalAmount: number; items: DefaulterItem[] };
}

type BucketKey = 'dueToday' | 'dueThisWeek' | 'dueThisMonth' | 'overdue';

const BUCKET_TABS: { key: BucketKey; label: string }[] = [
  { key: 'dueToday', label: 'Due Today' },
  { key: 'dueThisWeek', label: 'Due This Week' },
  { key: 'dueThisMonth', label: 'Due This Month' },
  { key: 'overdue', label: 'Overdue' },
];

const ACTIVITY_TYPES = ['CALL', 'VISIT', 'WHATSAPP', 'SMS', 'EMAIL', 'NOTE'];
const ACTIVITY_OUTCOMES = [
  'PROMISE_TO_PAY',
  'PARTIAL_PAID',
  'PAID',
  'NO_RESPONSE',
  'REFUSED',
  'DISPUTE',
  'OTHER',
];

const emptyActivity = (planId = '') => ({
  planId,
  type: 'CALL',
  outcome: 'NO_RESPONSE',
  notes: '',
  promiseDate: '',
  promisedAmount: 0,
});

export function RecoveryPage() {
  const { t } = useTranslation('customers');
  const { hasPermission } = useAuthStore();
  const canLog =
    hasPermission('installments:update') || hasPermission('pos:create') || hasPermission('installments:*');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<BucketKey>('overdue');
  const [activityDialog, setActivityDialog] = useState(false);
  const [activityForm, setActivityForm] = useState(emptyActivity());
  const [activityTarget, setActivityTarget] = useState<string>('');

  const { data, isLoading } = useQuery<RecoveryDashboard>({
    queryKey: ['recovery-dashboard'],
    queryFn: () => api.get('/recovery/dashboard').then((r) => r.data),
  });

  const logMutation = useMutation({
    mutationFn: (payload: typeof activityForm) =>
      api.post('/recovery/activities', {
        planId: payload.planId,
        type: payload.type,
        outcome: payload.outcome,
        notes: payload.notes || undefined,
        promiseDate: payload.promiseDate || undefined,
        promisedAmount: payload.promisedAmount > 0 ? payload.promisedAmount : undefined,
      }),
    onSuccess: () => {
      toast.success('Recovery activity logged');
      queryClient.invalidateQueries({ queryKey: ['recovery-dashboard'] });
      setActivityDialog(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to log activity'));
    },
  });

  const reminderMutation = useMutation({
    mutationFn: (planId: string) =>
      api.post(`/whatsapp/reminder/${planId}`).then(
        (r) =>
          r.data as { sent: boolean; link?: string; missingConfig?: string; configPath?: string },
      ),
    onSuccess: (data) => {
      if (data.sent) {
        toast.success('WhatsApp reminder sent');
        return;
      }
      if (data.link) window.open(data.link, '_blank', 'noopener');
      if (data.missingConfig && data.configPath) {
        toast((tt) => (
          <span className="flex items-center gap-2">
            WhatsApp API not configured — opened manual share.
            <button
              className="font-medium text-primary-text underline"
              onClick={() => { toast.dismiss(tt.id); navigate(data.configPath!); }}
            >
              Configure
            </button>
          </span>
        ), { icon: '⚙️', duration: 6000 });
      } else {
        toast.success('Opening WhatsApp…');
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to send reminder'));
    },
  });

  function openActivity(planId: string) {
    setActivityForm(emptyActivity(planId));
    setActivityTarget(planId);
    setActivityDialog(true);
  }

  const buckets = data
    ? [
        { key: 'dueToday' as const, label: 'Due Today', icon: CalendarClock, color: 'text-info-text', bucket: data.dueToday },
        { key: 'dueThisWeek' as const, label: 'Due This Week', icon: CalendarClock, color: 'text-primary-text', bucket: data.dueThisWeek },
        { key: 'dueThisMonth' as const, label: 'Due This Month', icon: CalendarClock, color: 'text-primary-text', bucket: data.dueThisMonth },
        { key: 'overdue' as const, label: 'Overdue', icon: AlertTriangle, color: 'text-destructive-text', bucket: data.overdue },
      ]
    : [];

  const activeBucket = data ? data[activeTab] : undefined;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Recovery" subtitle="Collection buckets, overdue installments & defaulter tracking" />

      {isLoading ? (
        <SkeletonTable rows={5} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {buckets.map(({ key, label, icon: Icon, color, bucket }) => (
              <Card key={key}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <Icon className={`h-5 w-5 ${color}`} />
                  </div>
                  <div className="mt-2 text-2xl font-semibold">{bucket.count}</div>
                  <div className="text-sm text-muted-foreground">{formatCurrency(bucket.totalAmount)}</div>
                </CardContent>
              </Card>
            ))}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Defaulters</span>
                  <Ban className="h-5 w-5 text-destructive-text" />
                </div>
                <div className="mt-2 text-2xl font-semibold">{data?.defaulters.count ?? 0}</div>
                <div className="text-sm text-muted-foreground">{formatCurrency(data?.defaulters.totalAmount ?? 0)}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-0">
              <TabBar
                tabs={BUCKET_TABS.map((t) => ({ key: t.key, label: t.label, count: data?.[t.key].count ?? 0 }))}
                activeTab={activeTab}
                onTabChange={(key) => setActiveTab(key as BucketKey)}
              />

              <div className="p-4">
                {!activeBucket || activeBucket.items.length === 0 ? (
                  <EmptyState icon={Coins} title="Nothing here" description="No installments in this bucket." />
                ) : (
                  <Table mobileCards>
                      <THead>
                        <Tr hover={false}>
                          <Th>{t('colCustomer')}</Th>
                          <Th>{t('colInvoice')}</Th>
                          <Th>{t('dueDate')}</Th>
                          <Th className="text-end">{t('colAmountDue')}</Th>
                          <Th>{t('colStatus')}</Th>
                          <Th className="text-end">{t('colAction')}</Th>
                        </Tr>
                      </THead>
                      <TBody>
                        {activeBucket.items.map((item) => (
                          <Tr key={item.paymentId}>
                            <Td className="td-name">
                              <div className="font-medium">{item.customerName ?? 'Walk-in'}</div>
                              {item.customerPhone && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Phone className="h-3 w-3" /> {item.customerPhone}
                                </div>
                              )}
                            </Td>
                            <Td label={t('colInvoice')}>{item.invoiceNumber ?? '—'}</Td>
                            <Td label={t('dueDate')}>{formatDate(item.dueDate)}</Td>
                            <Td label={t('colAmountDue')} className="text-end">{formatCurrency(item.amountDue)}</Td>
                            <Td label={t('colStatus')}>{item.status}</Td>
                            <Td className="td-actions text-end">
                              {canLog && (
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => reminderMutation.mutate(item.planId)}
                                    disabled={reminderMutation.isPending}
                                    title="Send WhatsApp reminder"
                                  >
                                    <MessageCircle className="me-1 h-3 w-3" /> Remind
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => openActivity(item.planId)}>
                                    <Plus className="me-1 h-3 w-3" /> Log
                                  </Button>
                                </div>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </TBody>
                  </Table>
                )}
              </div>
            </CardContent>
          </Card>

          {data && data.defaulters.items.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="mb-3 flex items-center gap-2 font-semibold">
                  <Ban className="h-4 w-4 text-destructive-text" /> Defaulters
                </h3>
                <Table mobileCards>
                    <THead>
                      <Tr hover={false}>
                        <Th>{t('colCustomer')}</Th>
                        <Th>{t('colInvoice')}</Th>
                        <Th className="text-end">{t('colRemaining')}</Th>
                        <Th className="text-end">{t('colAction')}</Th>
                      </Tr>
                    </THead>
                    <TBody>
                      {data.defaulters.items.map((d) => (
                        <Tr key={d.planId}>
                          <Td className="td-name">
                            <div className="font-medium">{d.customerName ?? 'Walk-in'}</div>
                            {d.customerPhone && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="h-3 w-3" /> {d.customerPhone}
                              </div>
                            )}
                          </Td>
                          <Td label={t('colInvoice')}>{d.invoiceNumber ?? '—'}</Td>
                          <Td label={t('colRemaining')} className="text-end">{formatCurrency(d.remainingAmount)}</Td>
                          <Td className="td-actions text-end">
                            {canLog && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => reminderMutation.mutate(d.planId)}
                                  disabled={reminderMutation.isPending}
                                  title="Send WhatsApp reminder"
                                >
                                  <MessageCircle className="me-1 h-3 w-3" /> Remind
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openActivity(d.planId)}>
                                  <Plus className="me-1 h-3 w-3" /> Log
                                </Button>
                              </div>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </TBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <Dialog
        header="Log Recovery Activity"
        visible={activityDialog}
        onHide={() => setActivityDialog(false)}
        style={{ width: '32rem' }}
        modal
      >
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block">Type</Label>
            <Dropdown
              value={activityForm.type}
              options={ACTIVITY_TYPES.map((v) => ({ label: v, value: v }))}
              onChange={(e) => setActivityForm((f) => ({ ...f, type: e.value }))}
              className="w-full"
            />
          </div>
          <div>
            <Label className="mb-1 block">Outcome</Label>
            <Dropdown
              value={activityForm.outcome}
              options={ACTIVITY_OUTCOMES.map((v) => ({ label: v.replace(/_/g, ' '), value: v }))}
              onChange={(e) => setActivityForm((f) => ({ ...f, outcome: e.value }))}
              className="w-full"
            />
          </div>
          {activityForm.outcome === 'PROMISE_TO_PAY' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block">Promise Date</Label>
                <Input
                  type="date"
                  value={activityForm.promiseDate}
                  onChange={(e) => setActivityForm((f) => ({ ...f, promiseDate: e.target.value }))}
                />
              </div>
              <div>
                <Label className="mb-1 block">Promised Amount</Label>
                <Input
                  type="number"
                  min={0}
                  value={activityForm.promisedAmount}
                  onChange={(e) => setActivityForm((f) => ({ ...f, promisedAmount: Number(e.target.value) }))}
                />
              </div>
            </div>
          )}
          <div>
            <Label className="mb-1 block">Notes</Label>
            <Textarea
              value={activityForm.notes}
              onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setActivityDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => logMutation.mutate({ ...activityForm, planId: activityTarget })}
              disabled={logMutation.isPending}
            >
              Save Activity
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
